import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { Check, Copy, Eye, EyeOff, Globe, Link2, Loader2, Lock, Plus, Trash2 } from 'lucide-react';
import { businessCasesService } from '../../services/api';

const fa = (n: number) => n.toLocaleString('fa-IR');

const inputClass =
  'w-full rounded-lg border border-gray-300 px-3 py-2 text-sm outline-none transition focus:border-blue-500';

/** پنل ادمین روی ساب‌دامین جداست، پس نمی‌شود از origin استفاده کرد */
const SITE_ORIGIN =
  process.env.REACT_APP_SITE_URL?.replace(/\/$/, '') || 'https://manehaghighi.com';

/**
 * لینک‌ها عمداً کوتاه‌اند چون با پیامک فرستاده می‌شوند و هر ۷۰ کاراکتر فارسی
 * یک بخش هزینه دارد. مسیر /6c و پارامتر ?i معادل /business-assessment و
 * ?invite هستند؛ لینک‌های قدیمی هم همچنان باز می‌شوند.
 */
const inviteLink = (code: string) => `${SITE_ORIGIN}/6c?i=${code}`;

/** لینک عمومی — بدون کد؛ فقط وقتی دسترسی روی «باز برای همه» باشد کار می‌کند */
const publicLink = () => `${SITE_ORIGIN}/6c`;

/** همان لینک بدون //:https — برای متن پیامک، چون گوشی خودش لینکش می‌کند */
const smsLink = (link: string) => link.replace(/^https?:\/\//, '');

interface Invite {
  id: string;
  code: string;
  label?: string | null;
  phone?: string | null;
  maxUses: number;
  usedCount: number;
  isActive: boolean;
  claims: Array<{ name: string | null; phone: string | null; completed: boolean }>;
}

interface FormInfo {
  id: string;
  title: string;
  slug: string;
  published: boolean;
  accessMode: 'PUBLIC' | 'INVITE_ONLY';
  caseCount: number;
  axes: Array<{ id: string; title: string; subtitle?: string | null; course?: { id: string; title: string } | null }>;
}

export const BusinessFormSettings: React.FC<{ courses: Array<{ id: string; title: string }> }> = ({
  courses,
}) => {
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState('');
  const [notice, setNotice] = useState('');

  const [form, setForm] = useState<FormInfo | null>(null);
  const [invites, setInvites] = useState<Invite[]>([]);
  const [copiedId, setCopiedId] = useState('');
  const [copiedPublic, setCopiedPublic] = useState(false);
  const [copiedSms, setCopiedSms] = useState(false);
  const [filter, setFilter] = useState<'all' | 'unused' | 'used' | 'completed'>('all');
  const [draft, setDraft] = useState({ phones: '', count: '1', label: '', maxUses: '1' });

  const flash = (m: string) => {
    setNotice(m);
    setTimeout(() => setNotice(''), 3000);
  };
  const fail = (err: any, fallback: string) => {
    setError(err?.response?.data?.message || fallback);
    setTimeout(() => setError(''), 6000);
  };

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const formData = await businessCasesService.getForm();
      setForm(formData);
      setInvites(await businessCasesService.listInvites(formData.id));
    } catch (err: any) {
      fail(err, 'دریافت اطلاعات فرم انجام نشد.');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    load();
  }, [load]);

  const stats = useMemo(() => {
    const used = invites.filter((i) => i.usedCount > 0).length;
    const completed = invites.filter((i) => i.claims.some((c) => c.completed)).length;
    return { total: invites.length, used, completed, unused: invites.length - used };
  }, [invites]);

  const visible = useMemo(() => {
    if (filter === 'unused') return invites.filter((i) => i.usedCount === 0);
    if (filter === 'used') return invites.filter((i) => i.usedCount > 0);
    if (filter === 'completed') return invites.filter((i) => i.claims.some((c) => c.completed));
    return invites;
  }, [invites, filter]);

  const run = async (fn: () => Promise<void>, fallback: string) => {
    setBusy(true);
    try {
      await fn();
    } catch (err: any) {
      fail(err, fallback);
    } finally {
      setBusy(false);
    }
  };

  const generate = () =>
    run(async () => {
      if (!form) return;
      const phones = draft.phones
        .split(/[\s,;\n]+/)
        .map((x) => x.trim())
        .filter(Boolean);

      const result = await businessCasesService.createInvites(form.id, {
        label: draft.label.trim() || undefined,
        maxUses: Number(draft.maxUses) || 1,
        ...(phones.length ? { phones } : { count: Number(draft.count) || 1 }),
      });
      setInvites(await businessCasesService.listInvites(form.id));
      setDraft((d) => ({ ...d, phones: '' }));

      /* شماره‌های ردشده باید دیده شوند؛ وگرنه ادمین فکر می‌کند همهٔ لیست لینک گرفته */
      const skipped = [
        result.duplicate?.length ? `${fa(result.duplicate.length)} تکراری` : '',
        result.invalid?.length ? `${fa(result.invalid.length)} نامعتبر` : '',
      ].filter(Boolean);
      flash(
        `${fa(result.created)} لینک ساخته شد.` +
          (skipped.length
            ? ` ${skipped.join(' و ')} ثبت نشد: ${(result.invalid ?? []).join('، ') || '—'}`
            : ''),
      );
    }, 'ساخت دعوت‌نامه انجام نشد.');

  const copyOne = async (invite: Invite) => {
    const link = inviteLink(invite.code);
    try {
      await navigator.clipboard.writeText(link);
      setCopiedId(invite.id);
      setTimeout(() => setCopiedId(''), 1800);
    } catch {
      window.prompt('لینک را کپی کنید:', link);
    }
  };

  /** خروجی «شماره<tab>لینک» که مستقیم در پنل پیامک نظیربه‌نظیر قابل چسباندن است */
  const copyAllUnused = async () => {
    const lines = invites
      .filter((i) => i.usedCount === 0 && i.isActive)
      /* خروجی مستقیم برای پنل پیامک؛ پس نسخهٔ کوتاه بدون //:https */
      .map((i) => (i.phone ? `${i.phone}\t${smsLink(inviteLink(i.code))}` : smsLink(inviteLink(i.code))))
      .join('\n');
    if (!lines) {
      setError('لینک استفاده‌نشده‌ای وجود ندارد.');
      return;
    }
    try {
      await navigator.clipboard.writeText(lines);
      flash('لینک‌های استفاده‌نشده کپی شد — هر خط: شماره و لینک.');
    } catch {
      window.prompt('کپی کنید:', lines);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center gap-2 rounded-xl border border-gray-200 bg-white py-16 text-sm font-bold text-gray-400">
        <Loader2 className="h-4 w-4 animate-spin" />
        در حال بارگذاری…
      </div>
    );
  }

  if (!form) {
    return (
      <div className="rounded-xl border border-gray-200 bg-white p-10 text-center text-sm font-bold text-gray-500">
        هنوز فرمی ثبت نشده است. روی سرور دستور
        <code className="mx-1 rounded bg-gray-100 px-1.5 py-0.5">npm run seed:business</code>
        را اجرا کنید.
      </div>
    );
  }

  const inviteOnly = form.accessMode === 'INVITE_ONLY';

  return (
    <div className="space-y-4">
      {error ? (
        <div className="rounded-lg border border-red-200 bg-red-50 px-4 py-3 text-sm font-bold text-red-700">
          {error}
        </div>
      ) : null}
      {notice ? (
        <div className="rounded-lg border border-green-200 bg-green-50 px-4 py-3 text-sm font-bold text-green-700">
          {notice}
        </div>
      ) : null}

      {/* وضعیت فرم */}
      <div className="rounded-xl border border-gray-200 bg-white p-4">
        <div className="flex flex-wrap items-center justify-between gap-3">
          <div>
            <p className="text-sm font-bold text-gray-900">{form.title}</p>
            <p className="mt-0.5 text-[11.5px] font-bold text-gray-500">
              {fa(form.caseCount)} پروندهٔ ثبت‌شده
            </p>
          </div>
          <button
            type="button"
            disabled={busy}
            onClick={() =>
              run(async () => {
                const updated = await businessCasesService.updateForm(form.id, {
                  published: !form.published,
                });
                setForm({ ...form, ...updated });
                flash(updated.published ? 'فرم منتشر شد.' : 'فرم به پیش‌نویس برگشت.');
              }, 'تغییر وضعیت انجام نشد.')
            }
            className={`flex items-center gap-1.5 rounded-lg px-3 py-2 text-sm font-bold ${
              form.published ? 'bg-green-600 text-white' : 'border border-gray-300 text-gray-700'
            }`}
          >
            {form.published ? <Eye className="h-4 w-4" /> : <EyeOff className="h-4 w-4" />}
            {form.published ? 'منتشر شده' : 'پیش‌نویس'}
          </button>
        </div>

        {!form.published ? (
          <p className="mt-3 rounded-lg border border-amber-200 bg-amber-50 px-3 py-2 text-[11.5px] font-bold text-amber-800">
            تا فرم منتشر نشود، هیچ لینکی برای مشتری باز نمی‌شود.
          </p>
        ) : null}

        {/* انتخاب حالت دسترسی — دو کارت به‌جای یک دکمهٔ مبهم */}
        <div className="mt-4 grid gap-3 md:grid-cols-2">
          {(
            [
              {
                mode: 'PUBLIC' as const,
                icon: <Globe className="h-4 w-4" />,
                title: 'یک لینک برای همه',
                text: 'هرکسی لینک را داشته باشد می‌تواند وارد شود و فرم را پر کند. برای ارسال گروهی در کانال، اینستاگرام یا پیامک تبلیغاتی.',
              },
              {
                mode: 'INVITE_ONLY' as const,
                icon: <Lock className="h-4 w-4" />,
                title: 'فقط با دعوت‌نامه',
                text: 'هر نفر لینک اختصاصی خودش را می‌گیرد. لینک شخصی فقط با شمارهٔ همان فرد باز می‌شود.',
              },
            ]
          ).map((option) => {
            const active = form.accessMode === option.mode;
            return (
              <button
                key={option.mode}
                type="button"
                disabled={busy}
                onClick={() =>
                  run(async () => {
                    if (active) return;
                    const updated = await businessCasesService.updateForm(form.id, {
                      accessMode: option.mode,
                    });
                    setForm({ ...form, ...updated });
                    flash('حالت دسترسی تغییر کرد.');
                  }, 'تغییر دسترسی انجام نشد.')
                }
                className={`rounded-xl border p-3 text-right transition ${
                  active ? 'border-blue-600 bg-blue-50' : 'border-gray-200 hover:border-gray-300'
                }`}
              >
                <span className="flex items-center gap-2">
                  <span className={active ? 'text-blue-700' : 'text-gray-400'}>{option.icon}</span>
                  <span className={`text-xs font-black ${active ? 'text-blue-800' : 'text-gray-700'}`}>
                    {option.title}
                  </span>
                  {active ? (
                    <span className="mr-auto rounded-full bg-blue-600 px-2 py-0.5 text-[10px] font-bold text-white">
                      فعال
                    </span>
                  ) : null}
                </span>
                <span className="mt-1.5 block text-[11px] font-bold leading-6 text-gray-500">
                  {option.text}
                </span>
              </button>
            );
          })}
        </div>
      </div>

      {/* لینک عمومی */}
      {!inviteOnly ? (
        <div className="rounded-xl border border-blue-200 bg-blue-50/50 p-4">
          <p className="flex items-center gap-2 text-sm font-bold text-gray-900">
            <Globe className="h-4 w-4 text-blue-600" />
            لینک عمومی فرم
          </p>
          <p className="mt-1 text-[11.5px] font-bold text-gray-600">
            همین یک لینک را برای همه بفرستید. کاربر بعد از ورود به حساب، مستقیم وارد فرم
            می‌شود و پاسخ‌هایش روی حساب خودش ثبت می‌شود.
          </p>
          <div className="mt-3 flex flex-wrap items-center gap-2">
            <code className="flex-1 overflow-x-auto rounded-lg border border-blue-200 bg-white px-3 py-2 text-xs font-bold text-gray-800">
              {publicLink()}
            </code>
            <button
              type="button"
              onClick={async () => {
                try {
                  await navigator.clipboard.writeText(publicLink());
                  setCopiedPublic(true);
                  setTimeout(() => setCopiedPublic(false), 1800);
                } catch {
                  window.prompt('لینک را کپی کنید:', publicLink());
                }
              }}
              className="flex shrink-0 items-center gap-1.5 rounded-lg bg-blue-600 px-4 py-2 text-sm font-bold text-white"
            >
              {copiedPublic ? <Check className="h-4 w-4" /> : <Copy className="h-4 w-4" />}
              {copiedPublic ? 'کپی شد' : 'کپی لینک'}
            </button>
          </div>

          {/* نسخهٔ پیامکی — هر ۷۰ کاراکتر فارسی یک بخش هزینه دارد */}
          <div className="mt-2 flex flex-wrap items-center gap-2">
            <code className="flex-1 overflow-x-auto rounded-lg border border-dashed border-blue-200 bg-white px-3 py-2 text-xs font-bold text-gray-800">
              {smsLink(publicLink())}
            </code>
            <button
              type="button"
              onClick={async () => {
                const short = smsLink(publicLink());
                try {
                  await navigator.clipboard.writeText(short);
                  setCopiedSms(true);
                  setTimeout(() => setCopiedSms(false), 1800);
                } catch {
                  window.prompt('لینک را کپی کنید:', short);
                }
              }}
              className="flex shrink-0 items-center gap-1.5 rounded-lg border border-blue-300 bg-white px-4 py-2 text-sm font-bold text-blue-700"
            >
              {copiedSms ? <Check className="h-4 w-4" /> : <Copy className="h-4 w-4" />}
              {copiedSms ? 'کپی شد' : 'کپی برای پیامک'}
            </button>
          </div>
          <p className="mt-1.5 text-[11px] font-bold text-gray-500">
            برای پیامک این نسخه ({fa(smsLink(publicLink()).length)} کاراکتر) را بگذارید؛ گوشی
            خودش لینکش می‌کند. متن فارسی هر ۷۰ کاراکتر یک بخش پیامک حساب می‌شود.
          </p>
        </div>
      ) : null}

      {/* اتصال محورها به دوره */}
      <div className="rounded-xl border border-gray-200 bg-white p-4">
        <p className="mb-1 text-sm font-bold text-gray-800">دورهٔ پیشنهادی هر محور</p>
        <p className="mb-3 text-[11.5px] font-bold text-gray-400">
          اختیاری — وقتی محوری ضعیف باشد، این دوره در پرونده به منتور پیشنهاد می‌شود.
        </p>
        <div className="grid gap-2 md:grid-cols-2">
          {form.axes.map((axis) => (
            <div key={axis.id} className="flex items-center gap-2 rounded-lg border border-gray-100 p-2">
              <div className="w-28 shrink-0">
                <p className="text-xs font-bold text-gray-800">{axis.title}</p>
                <p className="truncate text-[10px] text-gray-400">{axis.subtitle}</p>
              </div>
              <select
                value={axis.course?.id || ''}
                onChange={(e) =>
                  run(async () => {
                    await businessCasesService.linkAxisCourse(axis.id, e.target.value);
                    await load();
                  }, 'اتصال دوره انجام نشد.')
                }
                className={inputClass}
              >
                <option value="">— بدون دوره —</option>
                {courses.map((c) => (
                  <option key={c.id} value={c.id}>
                    {c.title}
                  </option>
                ))}
              </select>
            </div>
          ))}
        </div>
      </div>

      {/* ساخت لینک */}
      <div className="rounded-xl border border-gray-200 bg-white p-4">
        <p className="mb-1 text-sm font-bold text-gray-800">ساخت لینک دعوت</p>
        {!inviteOnly ? (
          <p className="mb-3 rounded-lg border border-amber-200 bg-amber-50 px-3 py-2 text-[11.5px] font-bold text-amber-800">
            الان فرم روی «یک لینک برای همه» است، پس این لینک‌ها لازم نیستند — لینک عمومی
            بالا کافی است. اینها فقط وقتی محدودیت دعوت‌نامه فعال باشد اثر دارند.
          </p>
        ) : null}
        <div className="grid gap-3 md:grid-cols-3">
          <label className="block">
            <span className="mb-1 block text-xs font-bold text-gray-600">برچسب (یادداشت)</span>
            <input
              value={draft.label}
              onChange={(e) => setDraft({ ...draft, label: e.target.value })}
              placeholder="مثلاً: دورهٔ مرداد"
              className={inputClass}
            />
          </label>
          <label className="block">
            <span className="mb-1 block text-xs font-bold text-gray-600">تعداد (بدون شماره)</span>
            <input
              type="number"
              min={1}
              value={draft.count}
              onChange={(e) => setDraft({ ...draft, count: e.target.value })}
              className={inputClass}
            />
          </label>
          <label className="block">
            <span className="mb-1 block text-xs font-bold text-gray-600">سقف استفادهٔ هر لینک</span>
            <input
              type="number"
              min={1}
              value={draft.maxUses}
              onChange={(e) => setDraft({ ...draft, maxUses: e.target.value })}
              className={inputClass}
            />
          </label>
        </div>

        <label className="mt-3 block">
          <span className="mb-1 block text-xs font-bold text-gray-600">
            یا لیست شماره موبایل — هر شماره یک لینک شخصی می‌گیرد که فقط با همان شماره باز می‌شود
          </span>
          <textarea
            value={draft.phones}
            onChange={(e) => setDraft({ ...draft, phones: e.target.value })}
            rows={4}
            placeholder="۰۹۱۲۱۲۳۴۵۶۷، ۰۹۳۵۱۱۱۲۲۳۳ — یا هر خط یک شماره (کپی مستقیم از اکسل هم کار می‌کند)"
            className={inputClass}
          />
        </label>

        <div className="mt-3 flex flex-wrap gap-2">
          <button
            type="button"
            disabled={busy}
            onClick={generate}
            className="flex items-center gap-1.5 rounded-lg bg-blue-600 px-4 py-2 text-sm font-bold text-white disabled:opacity-50"
          >
            <Plus className="h-4 w-4" />
            ساخت لینک
          </button>
          <button
            type="button"
            onClick={copyAllUnused}
            className="flex items-center gap-1.5 rounded-lg border border-gray-300 px-4 py-2 text-sm font-bold text-gray-700"
          >
            <Copy className="h-4 w-4" />
            کپی همهٔ لینک‌های استفاده‌نشده
          </button>
        </div>
      </div>

      {/* لیست */}
      <div className="rounded-xl border border-gray-200 bg-white">
        <div className="flex flex-wrap items-center gap-2 border-b border-gray-100 p-4">
          {(
            [
              ['all', `همه (${fa(stats.total)})`],
              ['unused', `ارسال‌نشده (${fa(stats.unused)})`],
              ['used', `باز شده (${fa(stats.used)})`],
              ['completed', `تکمیل‌شده (${fa(stats.completed)})`],
            ] as const
          ).map(([key, label]) => (
            <button
              key={key}
              type="button"
              onClick={() => setFilter(key)}
              className={`rounded-lg px-3 py-1.5 text-xs font-bold transition ${
                filter === key ? 'bg-blue-600 text-white' : 'bg-gray-100 text-gray-600'
              }`}
            >
              {label}
            </button>
          ))}
        </div>

        <div className="overflow-x-auto">
          <table className="w-full min-w-[760px] text-right text-sm">
            <thead className="bg-gray-50 text-[11px] font-bold text-gray-500">
              <tr>
                <th className="px-4 py-3">کد</th>
                <th className="px-4 py-3">برای</th>
                <th className="px-4 py-3">وضعیت</th>
                <th className="px-4 py-3">استفاده</th>
                <th className="px-4 py-3">عملیات</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-100">
              {visible.map((invite) => {
                const claim = invite.claims[0];
                return (
                  <tr key={invite.id} className={invite.isActive ? '' : 'opacity-50'}>
                    <td className="px-4 py-3">
                      <code className="rounded bg-gray-100 px-1.5 py-0.5 text-[11px] font-bold">
                        {invite.code}
                      </code>
                    </td>
                    <td className="px-4 py-3 text-xs">
                      {invite.phone ? (
                        <span className="font-bold text-gray-800">{invite.phone}</span>
                      ) : (
                        <span className="text-gray-400">عمومی</span>
                      )}
                      {invite.label ? (
                        <span className="block text-[10.5px] text-gray-400">{invite.label}</span>
                      ) : null}
                    </td>
                    <td className="px-4 py-3">
                      {claim?.completed ? (
                        <span className="rounded-full border border-green-200 bg-green-50 px-2 py-0.5 text-[10.5px] font-bold text-green-700">
                          فرم را تکمیل کرده
                        </span>
                      ) : claim ? (
                        <span className="rounded-full border border-amber-200 bg-amber-50 px-2 py-0.5 text-[10.5px] font-bold text-amber-700">
                          باز کرده، ناتمام
                        </span>
                      ) : (
                        <span className="rounded-full border border-gray-200 px-2 py-0.5 text-[10.5px] font-bold text-gray-400">
                          هنوز باز نشده
                        </span>
                      )}
                      {claim?.name ? (
                        <span className="mt-0.5 block text-[10.5px] text-gray-500">{claim.name}</span>
                      ) : null}
                    </td>
                    <td className="px-4 py-3 text-xs font-bold tabular-nums text-gray-600">
                      {fa(invite.usedCount)} / {fa(invite.maxUses)}
                    </td>
                    <td className="px-4 py-3">
                      <div className="flex items-center gap-1.5">
                        <button
                          type="button"
                          onClick={() => copyOne(invite)}
                          className="flex items-center gap-1 rounded-lg border border-gray-300 px-2 py-1 text-[11px] font-bold text-gray-700"
                        >
                          {copiedId === invite.id ? (
                            <>
                              <Check className="h-3 w-3 text-green-600" />
                              کپی شد
                            </>
                          ) : (
                            <>
                              <Link2 className="h-3 w-3" />
                              کپی لینک
                            </>
                          )}
                        </button>
                        <button
                          type="button"
                          onClick={() =>
                            run(async () => {
                              await businessCasesService.updateInvite(invite.id, {
                                isActive: !invite.isActive,
                              });
                              setInvites(await businessCasesService.listInvites(form.id));
                            }, 'تغییر وضعیت انجام نشد.')
                          }
                          className="rounded-lg border border-gray-300 px-2 py-1 text-[11px] font-bold text-gray-600"
                        >
                          {invite.isActive ? 'غیرفعال' : 'فعال'}
                        </button>
                        <button
                          type="button"
                          onClick={() =>
                            run(async () => {
                              if (!window.confirm('این لینک حذف شود؟')) return;
                              await businessCasesService.deleteInvite(invite.id);
                              setInvites(await businessCasesService.listInvites(form.id));
                            }, 'حذف انجام نشد.')
                          }
                          className="rounded-lg p-1 text-red-600 hover:bg-red-50"
                          aria-label="حذف"
                        >
                          <Trash2 className="h-3.5 w-3.5" />
                        </button>
                      </div>
                    </td>
                  </tr>
                );
              })}
              {!visible.length ? (
                <tr>
                  <td colSpan={5} className="px-4 py-10 text-center font-bold text-gray-400">
                    لینکی در این دسته نیست.
                  </td>
                </tr>
              ) : null}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
};
